import { useCallback, useEffect, useRef, useState } from "react";

import { DashboardWindow } from "./dashboardHelpers";
import type { ChatMode, RunStatus, UseChatMessagesResult } from "./useChatMessages";
import type { CoeffConsoleEvent } from "./useDashboardConsoleCapture";

type PendingMessage = {
  id: string;
  role: "user" | "agent";
  content: string;
};

export interface UseDashboardAutoFixOptions {
  enabled?: boolean;
  mode: ChatMode;
  targetWindow?: DashboardWindow;
  threadId: string | null;
  webDashboardId: string;
  runStatus: RunStatus;
  submitMessage: UseChatMessagesResult["submitMessage"];
  addPendingMessage: (message: PendingMessage) => void;
  setIsThinking: (value: boolean) => void;
  onAutoFixExhausted?: (summary: string) => void;
}

export interface UseDashboardAutoFixResult {
  /** True while an auto-fix message is being submitted (disable chat input). */
  isAutoFixInFlight: boolean;
  /** Call when we apply the first dashboard update message in a run. */
  onDashboardUpdated: () => void;
  /** Call when starting a new user turn (resets attempts + allows auto-fix). */
  resetForNewUserMessage: () => void;
}

type AutoFixCheckReason = "after_agent_update" | "after_silent_reload";

export function useDashboardAutoFix(
  options: UseDashboardAutoFixOptions
): UseDashboardAutoFixResult {
  const {
    enabled = true,
    mode,
    targetWindow,
    threadId,
    webDashboardId,
    runStatus,
    submitMessage,
    addPendingMessage,
    setIsThinking,
    onAutoFixExhausted,
  } = options;

  // Client-only latch: only attempt auto-fix after a user-initiated edit.
  const isAutoFixAllowed = useRef<boolean>(false);
  const autoFixAttempt = useRef<number>(0);
  const [isAutoFixInFlight, setIsAutoFixInFlight] = useState(false);
  const autoFixBaselineSeq = useRef<number | null>(null);
  // Ref: immediate latch for within-effect loops (baseline capture + "we have pending auto-fix").
  const hasPendingAutoFixRef = useRef<boolean>(false);
  // State: drives effects/dependency arrays.
  const [shouldAutoFix, setShouldAutoFix] = useState<boolean>(false);
  const autoFixTimeoutIdRef = useRef<number | null>(null);
  // Noise control: only log "waiting for run completion" once per runStatus value.
  const lastWaitLogRunStatusRef = useRef<RunStatus | null>(null);

  const clearAutoFixTimeout = useCallback(() => {
    if (autoFixTimeoutIdRef.current != null) {
      window.clearTimeout(autoFixTimeoutIdRef.current);
      autoFixTimeoutIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      return;
    }
    clearAutoFixTimeout();
    isAutoFixAllowed.current = false;
    autoFixAttempt.current = 0;
    setIsAutoFixInFlight(false);
    autoFixBaselineSeq.current = null;
    hasPendingAutoFixRef.current = false;
    setShouldAutoFix(false);
  }, [enabled, clearAutoFixTimeout]);

  const getNewConsoleEventsSince = useCallback(
    (baselineSeq: number): CoeffConsoleEvent[] => {
      const buf = targetWindow?.__coeffConsoleBuffer ?? [];
      return buf.filter(ev => (ev.seq ?? 0) > baselineSeq);
    },
    [targetWindow]
  );

  const buildErrorReportFromEvents = useCallback((newEvents: CoeffConsoleEvent[]) => {
    const lastErrorIndex = [...newEvents]
      .map((ev, idx) => ({ ev, idx }))
      .filter(({ ev }) => ev.level === "error")
      .map(({ idx }) => idx)
      .pop();

    if (lastErrorIndex == null) {
      return null;
    }

    const CONTEXT_BEFORE = 20;
    const start = Math.max(0, lastErrorIndex - CONTEXT_BEFORE);
    const events = newEvents.slice(start);
    return { events };
  }, []);

  const submitAutoFixMessage = useCallback(
    async (consoleReport: any) => {
      const attemptIndex = autoFixAttempt.current;
      const backendText =
        `[Auto-fix] The dashboard rendered with runtime errors. Attempt ${attemptIndex}/3.\n\n` +
        `Please fix the dashboard code based on this console/error report JSON:\n` +
        `${JSON.stringify(consoleReport, null, 2)}`;

      // Cosmetic UX: don't show raw console/error JSON to the user.
      const visibleText =
        `[Auto-fix] Some dashboard blocks didn't render correctly. Fixing it now… ` +
        `(Attempt ${attemptIndex}/3)`;

      // Mirror normal UX: show a synthetic user message immediately.
      addPendingMessage({
        id: `pending-autofix-user-${Date.now()}`,
        role: "user",
        content: visibleText,
      });
      try {
        setIsThinking(true);
        await submitMessage(backendText, undefined, undefined, {
          auto_fix: {
            is_auto_fix: true,
            attempt_index: attemptIndex,
            max_attempts: 3,
          },
        });
      } catch (e) {
        // If the auto-fix request fails to submit (network/error), ensure we don't stay stuck in
        // the thinking state. On success, `isThinking` stays true until the agent run completes.
        setIsThinking(false);
        throw e;
      }
    },
    [addPendingMessage, setIsThinking, submitMessage]
  );

  const scheduleAutoFixCheck = useCallback(
    (reason: AutoFixCheckReason) => {
      if (!enabled) {
        return;
      }
      if (mode !== "editor" || !targetWindow || !threadId) {
        console.debug("[vibedash][autofix] skip: missing mode/targetWindow/threadId", {
          mode,
          hasTargetWindow: Boolean(targetWindow),
          threadId,
        });
        return;
      }
      if (!isAutoFixAllowed.current) {
        console.debug("[vibedash][autofix] skip: isAutoFixAllowed=false");
        return;
      }

      clearAutoFixTimeout();

      // Baseline is recorded when we apply the first dashboard update in a run (so we
      // include render-time errors that happen immediately after the update).
      const baselineSeq = autoFixBaselineSeq.current ?? targetWindow.__coeffConsoleSeq ?? 0;
      console.debug("[vibedash][autofix] scheduled", {
        reason,
        baselineSeq,
        currentSeq: targetWindow.__coeffConsoleSeq ?? 0,
        bufferSize: targetWindow.__coeffConsoleBuffer?.length ?? 0,
      });

      autoFixTimeoutIdRef.current = window.setTimeout(async () => {
        const newEvents = getNewConsoleEventsSince(baselineSeq);
        const built = buildErrorReportFromEvents(newEvents);
        const lastErr = [...newEvents].reverse().find(ev => ev.level === "error");
        console.debug("[vibedash][autofix] check window complete", {
          baselineSeq,
          newEventsCount: newEvents.length,
          hasError: Boolean(lastErr),
          lastErrorPreview: lastErr
            ? { seq: lastErr.seq, message: lastErr.message, stack: lastErr.stack }
            : null,
        });
        if (!built) {
          // No errors observed in this window
          setIsAutoFixInFlight(false);
          return;
        }

        if (autoFixAttempt.current >= 3) {
          onAutoFixExhausted?.("Auto-fix couldn't resolve the dashboard error after 3 attempts.");
          setIsAutoFixInFlight(false);
          return;
        }

        autoFixAttempt.current += 1;
        setIsAutoFixInFlight(true);

        const consoleReport = {
          schema_version: 1,
          dashboard_id: webDashboardId,
          thread_id: threadId,
          client_ts_ms: Date.now(),
          attempt_index: autoFixAttempt.current,
          reason,
          events: built.events,
        };

        try {
          await submitAutoFixMessage(consoleReport);
          // The chat input is already disabled while the agent run is in progress via `isThinking`.
          // Clear this flag after successfully submitting the auto-fix message so we don't get
          // stuck disabled if the agent run completes without producing any dashboard updates.
          setIsAutoFixInFlight(false);
        } catch (e) {
          console.error("[vibedash] Failed to submit auto-fix message", e);
          setIsAutoFixInFlight(false);
        }
      }, 3000);
    },
    [
      enabled,
      mode,
      targetWindow,
      threadId,
      webDashboardId,
      getNewConsoleEventsSince,
      buildErrorReportFromEvents,
      submitAutoFixMessage,
      onAutoFixExhausted,
      clearAutoFixTimeout,
    ]
  );

  const onDashboardUpdated = useCallback(() => {
    if (!enabled) {
      return;
    }
    // Capture baseline once per user turn. We intentionally do NOT re-capture after the run
    // completes, because late-arriving dashboard updates (e.g. after screenshot tool calls)
    // can otherwise advance the baseline past earlier render-time errors.
    if (autoFixBaselineSeq.current == null) {
      autoFixBaselineSeq.current = targetWindow?.__coeffConsoleSeq ?? 0;
    }

    // The agent may emit multiple incremental updates in a single run; we only want to
    // run the auto-fix check once the run completes (so we check the final rendered state).
    hasPendingAutoFixRef.current = true;
    setShouldAutoFix(true);
  }, [enabled, targetWindow]);

  // After a (non-silent) agent run completes, check console for runtime errors and trigger auto-fix.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (mode !== "editor") {
      return;
    }
    if (!isAutoFixAllowed.current) {
      return;
    }
    if (!shouldAutoFix) {
      return;
    }
    if (runStatus !== "completed") {
      if (lastWaitLogRunStatusRef.current !== runStatus) {
        console.debug("[vibedash][autofix] wait: run not completed yet", { runStatus });
        lastWaitLogRunStatusRef.current = runStatus;
      }
      return;
    }
    lastWaitLogRunStatusRef.current = null;

    hasPendingAutoFixRef.current = false;
    setShouldAutoFix(false);
    scheduleAutoFixCheck("after_agent_update");
  }, [enabled, runStatus, mode, shouldAutoFix, scheduleAutoFixCheck]);

  const resetForNewUserMessage = useCallback(() => {
    if (!enabled) {
      return;
    }
    clearAutoFixTimeout();

    // Allow auto-fix for this user-initiated edit.
    isAutoFixAllowed.current = true;
    autoFixAttempt.current = 0;
    setIsAutoFixInFlight(false);
    autoFixBaselineSeq.current = null;
    hasPendingAutoFixRef.current = false;
    setShouldAutoFix(false);
  }, [enabled, clearAutoFixTimeout]);

  // Ensure no stray timeout survives unmount.
  useEffect(() => clearAutoFixTimeout, [clearAutoFixTimeout]);

  return {
    isAutoFixInFlight,
    onDashboardUpdated,
    resetForNewUserMessage,
  };
}
